// src/pages/admin-audit-management/components/AssignmentConfirmationModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabaseAdmin } from '../../../lib/supabase';

const AssignmentConfirmationModal = ({ isOpen, onClose, onConfirm, assignments, uploadedFiles, users, isProcessing }) => {
  const [assignmentDetails, setAssignmentDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());

  const currentSessionRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      currentSessionRef.current = Date.now().toString();
      setAssignmentDetails([]);
      setError(null);
      setSelectedItems(new Set());

      if (Object.keys(assignments || {}).length > 0) {
        fetchAuditDetails(currentSessionRef.current);
      }
    } else {
      setAssignmentDetails([]);
      setError(null);
      setLoading(false);
      setSelectedItems(new Set());
      currentSessionRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && Object.keys(assignments || {}).length > 0) {
      const sessionId = Date.now().toString();
      currentSessionRef.current = sessionId;
      setAssignmentDetails([]);
      setError(null);
      setSelectedItems(new Set());
      fetchAuditDetails(sessionId);
    }
  }, [assignments]);

  const fetchAuditDetails = async (sessionId) => {
    setLoading(true);
    setError(null);

    const currentAssignments = { ...assignments };
    const details = [];

    try {
      for (const [fileId, assignmentValue] of Object.entries(currentAssignments)) {
        if (sessionId !== currentSessionRef.current) return;

        const isNewFormat = typeof assignmentValue === 'object' && assignmentValue !== null;
        const userId = isNewFormat ? assignmentValue.userId : assignmentValue;
        const submissionIdFromAssignment = isNewFormat ? assignmentValue.submissionId : null;

        const file = uploadedFiles?.find(f => f?.id === fileId);
        const user = users?.find(u => u?.id === userId);

        if (!file || !user) {
          console.warn(`⚠️ File or user not found:`, { fileId, userId });
          continue;
        }

        let auditType = user?.auditType || 'free';
        let submissionId = submissionIdFromAssignment || user?.submissionId || null;
        let requestId = user?.requestId || null;
        let companyName = file?.company_name || 'Unknown';
        let actualAuditId = null;

        const storagePath = file?.full_path || file?.html_storage_path || file?.storagePath;

        if (storagePath) {
          const { data, error: dbError } = await supabaseAdmin
            .from('audit_reports')
            .select('id, report_type, submission_id, company_name, json_data, created_at')
            .eq('html_storage_path', storagePath)
            .maybeSingle();

          if (!dbError && data) {
            actualAuditId = data.id;
            companyName = data.company_name || companyName;
            if (!submissionId) submissionId = data.submission_id;

            if (
              data.report_type?.toLowerCase() === 'paid' ||
              data.json_data?.report_type?.toLowerCase() === 'paid' ||
              data.json_data?.cover?.REPORT_TYPE?.toLowerCase() === 'paid'
            ) {
              auditType = 'paid';
            }
          }
        }

        if (!actualAuditId && file?.auditId) {
          actualAuditId = file.auditId;
          auditType = file.auditType || auditType;
        }

        if (user?.auditType === 'paid') auditType = 'paid';

        details.push({
          fileId,
          fileName: file?.name || file?.file_name || storagePath?.split('/').pop() || 'Unknown file',
          userId,
          userName: user?.name || user?.email || 'Unknown User',
          userEmail: user?.email,
          submissionId,
          requestId,
          auditId: actualAuditId,
          auditType,
          companyName,
          file,
          full_path: storagePath,
        });
      }

      if (sessionId !== currentSessionRef.current) return;

      // Deduplicate by fileId
      const seen = new Set();
      const deduped = details.filter(d => {
        if (seen.has(d.fileId)) return false;
        seen.add(d.fileId);
        return true;
      });

      setAssignmentDetails(deduped);
      setSelectedItems(new Set(deduped.filter(d => d.auditId).map(d => d.fileId)));

    } catch (err) {
      if (sessionId === currentSessionRef.current) {
        console.error('Error fetching audit details:', err);
        setError('Failed to load audit details. Please try again.');
      }
    } finally {
      if (sessionId === currentSessionRef.current) setLoading(false);
    }
  };

  const handleToggleItem = (fileId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      next.has(fileId) ? next.delete(fileId) : next.add(fileId);
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelectedItems(
      selectedItems.size === assignmentDetails.length
        ? new Set()
        : new Set(assignmentDetails.map(d => d.fileId))
    );
  };

  const handleConfirm = () => {
    const selectedDetails = assignmentDetails.filter(d => selectedItems.has(d.fileId));

    const missing = selectedDetails.filter(d => !d.auditId);
    if (missing.length > 0) {
      setError(`Could not find audit record for: ${missing.map(d => d.fileName).join(', ')}. Please refresh and try again.`);
      return;
    }

    if (selectedDetails.length === 0) {
      setError('Please select at least one audit to assign.');
      return;
    }

    // ✅ Just call onConfirm — the parent (AdminAuditManagement) shows the success toast.
    // DO NOT call toast.success() here — that was causing 2-3 duplicate popups.
    onConfirm(selectedDetails);
    handleClose();
  };

  const handleClose = () => {
    setAssignmentDetails([]);
    setError(null);
    setLoading(false);
    setSelectedItems(new Set());
    currentSessionRef.current = null;
    onClose();
  };

  if (!isOpen) return null;

  const paidCount = assignmentDetails.filter(d => d.auditType === 'paid').length;
  const validCount = assignmentDetails.filter(d => d.auditId).length;
  const invalidCount = assignmentDetails.length - validCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Confirm Assignment</h2>
              <p className="text-sm text-muted-foreground">
                {loading ? 'Loading audit details...' : 'Review and confirm assignments'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} disabled={isProcessing || loading}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Fetching audit details...</span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <Icon name="Info" size={16} color="var(--color-primary)" />
                <p className="text-sm text-primary flex-1">
                  Found <strong>{assignmentDetails.length}</strong> audit report
                  {assignmentDetails.length !== 1 ? 's' : ''} ready for assignment.
                  {validCount > 0 && ` ${validCount} can be assigned.`}
                  {invalidCount > 0 && (
                    <span className="text-destructive ml-1">{invalidCount} missing audit records.</span>
                  )}
                </p>
              </div>

              {/* Select All */}
              {assignmentDetails.length > 0 && (
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.size === assignmentDetails.length}
                      onChange={handleToggleAll}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-foreground">Select All</span>
                  </label>
                  <span className="text-xs text-muted-foreground">
                    {selectedItems.size} of {assignmentDetails.length} selected
                  </span>
                </div>
              )}

              <div className="space-y-3">
                {assignmentDetails.map((detail) => (
                  <div
                    key={detail.fileId}
                    className={`p-4 rounded-lg border transition-colors ${
                      selectedItems.has(detail.fileId)
                        ? 'bg-primary/5 border-primary/30'
                        : 'bg-muted/30 border-border'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(detail.fileId)}
                        onChange={() => handleToggleItem(detail.fileId)}
                        disabled={!detail.auditId}
                        className="mt-1 rounded border-border text-primary focus:ring-primary disabled:opacity-50"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-foreground">{detail.fileName}</p>
                            {detail.companyName && detail.companyName !== 'Unknown' && (
                              <p className="text-xs text-muted-foreground">{detail.companyName}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-background rounded-full border border-border">
                            <Icon name="FileText" size={14} color="var(--color-primary)" />
                            <span className="text-xs font-medium text-foreground">HTML</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">User</p>
                            <p className="text-sm font-medium text-foreground">{detail.userName}</p>
                            <p className="text-xs text-muted-foreground">{detail.userEmail}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                              <Icon name="Hash" size={11} /> Request ID
                            </p>
                            <p className="text-sm font-mono font-semibold text-primary">
                              {detail.requestId || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Audit Type</p>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                              detail.auditType === 'paid'
                                ? 'bg-success/10 text-success border border-success/20'
                                : 'bg-muted text-muted-foreground border border-border'
                            }`}>
                              <Icon name={detail.auditType === 'paid' ? 'CreditCard' : 'Gift'} size={12} />
                              {detail.auditType === 'paid' ? 'Paid Audit' : 'Free Audit'}
                            </span>
                          </div>
                          {detail.submissionId && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                                <Icon name="Link" size={11} /> Submission ID
                              </p>
                              <p className="text-xs font-mono text-muted-foreground truncate" title={detail.submissionId}>
                                {detail.submissionId.substring(0, 16)}...
                              </p>
                            </div>
                          )}
                        </div>

                        {detail.auditId ? (
                          <div className="mt-2 pt-2 border-t border-border text-xs text-success flex items-center gap-1">
                            <Icon name="CheckCircle2" size={10} />
                            Ready to assign — Audit ID: {detail.auditId.substring(0, 8)}...
                          </div>
                        ) : (
                          <div className="mt-2 pt-2 border-t border-destructive/20 text-xs text-destructive flex items-center gap-1">
                            <Icon name="AlertCircle" size={10} />
                            Cannot assign — Audit record not found in database
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <Icon name="AlertCircle" size={18} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-warning">Important Notice</p>
                  <p className="text-xs text-warning/80">
                    Once confirmed, selected audits will be assigned to users and appear in their dashboards.
                    {paidCount > 0 && ' Paid audits will be marked as premium.'} This action cannot be undone.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={handleClose} disabled={isProcessing || loading}>
            Cancel
          </Button>
          <Button
            variant="default"
            iconName="Send"
            iconPosition="left"
            onClick={handleConfirm}
            loading={isProcessing || loading}
            disabled={loading || selectedItems.size === 0}
          >
            {isProcessing ? 'Assigning...' : loading ? 'Loading...' : `Confirm & Assign (${selectedItems.size})`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentConfirmationModal;